import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { fetchShopProducts, absoluteShopUrl } from "../shop/api";
import { useCart } from "../shop/CartContext";
import { useSEO } from "../lib/seo";
import { useCmsSeo } from "../lib/cmsSeo";
import { toast } from "sonner";

export default function ShopPage() {
  const seo = useCmsSeo("shop", {
    title: "Shop — GenieX Smart Home Devices",
    description: "Shop GenieX smart switches, locks, doorbells, sensors and automation devices. Fast, secure checkout.",
    path: "/shop",
    keywords: "buy smart home devices India, GenieX shop, smart switch, smart lock",
  });
  useSEO(seo);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const cart = useCart();

  useEffect(() => {
    fetchShopProducts()
      .then((d) => setProducts(d.products || []))
      .catch(() => toast.error("Could not load products"))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["all", ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    if (category === "all") return products;
    return products.filter((p) => p.category === category);
  }, [products, category]);

  return (
    <main data-testid="shop-page" className="bg-[var(--gx-bg)] text-white">
      <section className="relative pt-32 lg:pt-40 pb-14 lg:pb-16 grain overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,169,97,0.12),transparent_60%)]" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
          <p className="eyebrow mb-4">The GenieX Shop</p>
          <h1 className="h1-display max-w-[20ch]">
            Bring intelligence <span className="text-gold">home, today.</span>
          </h1>
          <p className="lead mt-6 max-w-[52ch]">
            Curated smart-home devices, ready to ship. Secure checkout, tracked delivery, GenieX warranty.
          </p>
        </div>
      </section>

      <section className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 lg:py-16">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="shop-category-filters">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                data-testid={`shop-category-${c.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-[12.5px] tracking-wide border transition-colors ${
                  category === c ? "border-gold text-gold bg-gold/10" : "border-line text-dim hover:text-white hover:border-white/30"
                }`}
              >
                {c === "all" ? "All Products" : c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-dim text-sm py-20 text-center" data-testid="shop-loading">
              Loading products…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-dim text-sm py-20 text-center" data-testid="shop-empty">
              No products in this category yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((p) => (
                <div key={p.id} data-testid={`shop-product-card-${p.slug}`} className="card-lux overflow-hidden group">
                  <Link to={`/shop/${p.slug}`} data-testid={`shop-view-product-${p.slug}`} className="block aspect-[4/3] overflow-hidden bg-[var(--gx-surface-2)]">
                    {p.images?.[0] ? (
                      <img
                        src={absoluteShopUrl(p.images[0])}
                        alt={p.name}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-mute">
                        <ShoppingBag className="w-8 h-8" />
                      </div>
                    )}
                  </Link>
                  <div className="p-6">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-mute mb-2">{p.category}</p>
                    <Link to={`/shop/${p.slug}`} className="font-display text-lg block hover:text-gold transition-colors">
                      {p.name}
                    </Link>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="text-gold font-display text-xl">₹{Number(p.price).toLocaleString("en-IN")}</span>
                      {p.compare_at_price ? (
                        <span className="text-mute text-sm line-through">
                          ₹{Number(p.compare_at_price).toLocaleString("en-IN")}
                        </span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      disabled={p.stock <= 0}
                      onClick={() => {
                        cart.addItem(p, 1);
                        toast.success(`${p.name} added to cart`);
                      }}
                      data-testid={`shop-add-to-cart-${p.id}`}
                      className="btn-outline w-full mt-5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {p.stock <= 0 ? (
                        "Out of Stock"
                      ) : (
                        <>
                          Add to Cart <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
